/**
 * Metric value type classification and formatting utilities
 * Ensures consistent unit display across flame graph components
 */

export enum MetricValueType {
    PERCENTAGE,    // Divide by total, show %
    COUNT,         // Display raw value, no unit
    MICROSECONDS,  // Display raw value, show μs
    MILLISECONDS   // Display raw value, show ms (timeline only)
}

/**
 * Determines the value type for a given metric
 * @param metric - Metric ID (0-7)
 * @param metricContext - Optional context ('timeline', 'commit', 'api')
 * @returns The metric value type
 */
export function getMetricValueType(metric: number, metricContext?: string): MetricValueType {
    // Timeline context always shows milliseconds
    if (metricContext === 'timeline') {
        return MetricValueType.MILLISECONDS;
    }

    // Count-based: Only Exceptions (metric 2)
    // User confirmed SqlCount (4) should display as percentage
    if (metric === 2 || metric === 8) {
        return MetricValueType.COUNT;
    }

    // API metrics: microseconds
    if (metric === 6 || metric === 7) {
        return MetricValueType.MICROSECONDS;
    }

    // Default: percentage (OnCPU, OffCPU, Memory, SqlCount, SqlDuration)
    return MetricValueType.PERCENTAGE;
}

/**
 * Formats a metric value with appropriate units based on metric type
 * @param value - Raw metric value
 * @param metric - Metric ID (0-7)
 * @param metricContext - Optional context ('timeline', 'commit', 'api')
 * @param unfilteredTotal - Optional total for percentage calculation
 * @returns Formatted value string with units
 */
export function formatMetricValue(
    value: number,
    metric: number,
    metricContext?: string,
    unfilteredTotal?: number
): string {
    const valueType = getMetricValueType(metric, metricContext);

    switch (valueType) {
        case MetricValueType.PERCENTAGE:
            // For percentage metrics, calculate percentage from raw value
            if (unfilteredTotal && unfilteredTotal > 0) {
                const percent = (value * 100.0) / unfilteredTotal;
                return `${percent.toFixed(2)}%`;
            }
            // Fallback if no total provided (shouldn't happen in normal use)
            return `${value.toFixed(2)}%`;

        case MetricValueType.COUNT:
            // Display raw count without units, with locale formatting for large numbers
            return value.toLocaleString('en-US', { maximumFractionDigits: 0 });

        case MetricValueType.MICROSECONDS:
            return `${value.toFixed(2)} μs`;

        case MetricValueType.MILLISECONDS:
            return `${value.toFixed(2)} ms`;

        default:
            return value.toFixed(2);
    }
}

/**
 * Checks if a metric is count-based (displays raw counts, not percentages)
 * @param metric - Metric ID (0-7)
 * @returns True if the metric is count-based
 */
export function isCountBasedMetric(metric: number): boolean {
    return metric === 2 || metric === 8;
}

/**
 * Checks if a metric should display raw values (not percentages) in ProfileStats
 * This includes count metrics, time metrics in microseconds, etc.
 * @param metric - Metric ID (0-7)
 * @returns True if the metric should show raw values, not percentages
 */
export function isRawValueMetric(metric: number): boolean {
    // Exceptions (count), ApiCpu (μs), ApiDuration (μs)
    return metric === 2 || metric === 6 || metric === 7 || metric === 8;
}
